import type {
  BrowserNotificationCapabilities,
  NotificationPermissionState,
} from './browser-notification-runtime'

export type InstallationPlatformHint = 'ios' | 'android' | 'other'

export type InstallationBrowserHint
  = | 'safari'
    | 'chrome'
    | 'firefox'
    | 'samsung'
    | 'other'

export interface InstallationGuidanceHints {
  platform: InstallationPlatformHint
  browser: InstallationBrowserHint
}

export interface InstallationNavigatorSource {
  userAgent: string
  platform?: string
  maxTouchPoints?: number
}

export function inferInstallationGuidanceHints(
  source: InstallationNavigatorSource,
): InstallationGuidanceHints {
  const iosDevice
    = /iPad|iPhone|iPod/i.test(source.userAgent)
      || (source.platform === 'MacIntel' && (source.maxTouchPoints ?? 0) > 1)
  const androidDevice = /Android/i.test(source.userAgent)

  let browser: InstallationBrowserHint = 'other'
  if (/SamsungBrowser/i.test(source.userAgent)) {
    browser = 'samsung'
  }
  else if (/FxiOS|Firefox/i.test(source.userAgent)) {
    browser = 'firefox'
  }
  else if (/CriOS/i.test(source.userAgent)) {
    browser = 'chrome'
  }
  else if (/EdgiOS|EdgA|OPiOS|OPR/i.test(source.userAgent)) {
    browser = 'other'
  }
  else if (/Chrome|Chromium/i.test(source.userAgent)) {
    browser = 'chrome'
  }
  else if (/Safari/i.test(source.userAgent)) {
    browser = 'safari'
  }

  return {
    platform: iosDevice ? 'ios' : androidDevice ? 'android' : 'other',
    browser,
  }
}

export type InstallationInstructionFamily
  = | 'ios-safari'
    | 'ios-chrome'
    | 'ios-other'
    | 'android-prompt'
    | 'android-chrome'
    | 'android-firefox'
    | 'android-samsung'
    | 'android-other'
    | 'generic'

export type ManualInstallationInstructionFamily = Exclude<
  InstallationInstructionFamily,
  'android-prompt'
>

export const MANUAL_INSTALLATION_INSTRUCTION_FAMILIES = [
  'ios-safari',
  'ios-chrome',
  'ios-other',
  'android-chrome',
  'android-firefox',
  'android-samsung',
  'android-other',
  'generic',
] as const satisfies readonly ManualInstallationInstructionFamily[]

export interface InstallationInstructionContent {
  familyLabelKey: string
  stepKeys: readonly string[]
  fallbackKey: string | null
}

const IPHONE_STEP_KEYS = [
  'installationGuidance.iphoneSteps.share',
  'installationGuidance.iphoneSteps.addToHomeScreen',
  'installationGuidance.iphoneSteps.openAsWebApp',
  'installationGuidance.iphoneSteps.add',
  'installationGuidance.iphoneSteps.launch',
] as const

const ANDROID_COMMON_STEP_KEYS = [
  'installationGuidance.androidSteps.confirm',
  'installationGuidance.androidSteps.launch',
] as const

const FAMILY_LABEL_KEYS: Record<InstallationInstructionFamily, string> = {
  'ios-safari': 'installationGuidance.families.iosSafari',
  'ios-chrome': 'installationGuidance.families.iosChrome',
  'ios-other': 'installationGuidance.families.iosOther',
  'android-prompt': 'installationGuidance.families.androidPrompt',
  'android-chrome': 'installationGuidance.families.androidChrome',
  'android-firefox': 'installationGuidance.families.androidFirefox',
  'android-samsung': 'installationGuidance.families.androidSamsung',
  'android-other': 'installationGuidance.families.androidOther',
  'generic': 'installationGuidance.families.generic',
}

export function installationInstructionContent(
  family: InstallationInstructionFamily,
): InstallationInstructionContent {
  if (family.startsWith('ios-')) {
    return {
      familyLabelKey: FAMILY_LABEL_KEYS[family],
      stepKeys: IPHONE_STEP_KEYS,
      fallbackKey:
        family === 'ios-safari'
          ? null
          : 'installationGuidance.iphoneSteps.safariFallback',
    }
  }

  if (family === 'android-prompt') {
    return {
      familyLabelKey: FAMILY_LABEL_KEYS[family],
      stepKeys: [
        'installationGuidance.androidSteps.prompt',
        ...ANDROID_COMMON_STEP_KEYS,
      ],
      fallbackKey: null,
    }
  }

  const androidMenuKey: Partial<Record<InstallationInstructionFamily, string>>
    = {
      'android-chrome': 'installationGuidance.androidSteps.menuChrome',
      'android-firefox': 'installationGuidance.androidSteps.menuFirefox',
      'android-samsung': 'installationGuidance.androidSteps.menuSamsung',
      'android-other': 'installationGuidance.androidSteps.menuOther',
    }
  const menuKey = androidMenuKey[family]
  if (menuKey) {
    return {
      familyLabelKey: FAMILY_LABEL_KEYS[family],
      stepKeys: [menuKey, ...ANDROID_COMMON_STEP_KEYS],
      fallbackKey:
        family === 'android-chrome'
          ? null
          : 'installationGuidance.androidSteps.chromeFallback',
    }
  }

  return {
    familyLabelKey: FAMILY_LABEL_KEYS.generic,
    stepKeys: [
      'installationGuidance.genericSteps.openMenu',
      'installationGuidance.genericSteps.findInstall',
      'installationGuidance.genericSteps.launch',
    ],
    fallbackKey: 'installationGuidance.genericSteps.supportedBrowserFallback',
  }
}

export interface InstallationGuidanceInput {
  capabilities: Omit<BrowserNotificationCapabilities, 'lifecycleLock'> & {
    lifecycleLock?: boolean
  }
  permission: NotificationPermissionState
  installPromptAvailable: boolean
  hints: InstallationGuidanceHints
  manualOverride: ManualInstallationInstructionFamily | null
}

export interface InstallationGuidanceResolution {
  phase: 'standalone' | 'api-surface-exposed' | 'api-surface-incomplete'
  instructionFamily: InstallationInstructionFamily | null
  notificationApiSurfaceExposed: boolean
  installAction: 'none' | 'browser-prompt' | 'manual'
}

function inferredInstructionFamily(
  hints: InstallationGuidanceHints,
  installPromptAvailable: boolean,
): InstallationInstructionFamily {
  if (installPromptAvailable)
    return 'android-prompt'

  if (hints.platform === 'ios') {
    if (hints.browser === 'safari')
      return 'ios-safari'
    if (hints.browser === 'chrome')
      return 'ios-chrome'
    return 'ios-other'
  }

  if (hints.platform === 'android') {
    if (hints.browser === 'chrome')
      return 'android-chrome'
    if (hints.browser === 'firefox')
      return 'android-firefox'
    if (hints.browser === 'samsung')
      return 'android-samsung'
    return 'android-other'
  }

  return 'generic'
}

export function resolveInstallationGuidance(
  input: InstallationGuidanceInput,
): InstallationGuidanceResolution {
  const notificationApiSurfaceExposed
    = input.capabilities.serviceWorker
      && input.capabilities.push
      && input.capabilities.notification

  if (input.capabilities.standalone) {
    return {
      phase: 'standalone',
      instructionFamily: null,
      notificationApiSurfaceExposed,
      installAction: 'none',
    }
  }

  const instructionFamily
    = input.manualOverride
      ?? inferredInstructionFamily(input.hints, input.installPromptAvailable)

  return {
    phase: notificationApiSurfaceExposed
      ? 'api-surface-exposed'
      : 'api-surface-incomplete',
    instructionFamily,
    notificationApiSurfaceExposed,
    installAction: input.installPromptAvailable ? 'browser-prompt' : 'manual',
  }
}
